<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import directus from '../composables/useDirectus.js';
import { useCollectionsStore } from '../store/collections.js';

const props = defineProps({
  context: Object,
});

const field = ref('');
const response = ref([]);
const optionsLoaded = ref(false);

// Stable hash for the query so two dropdowns with the same args share the cache.
function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
  const keys = Object.keys(value).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
}

onMounted(async () => {
  try {
    const query = {
      fields: props.context.fields,
      limit: props.context.limit,
      sort: props.context.sort,
    };
    // Only include filter if it's defined
    if (props.context.filter) {
      query.filter = props.context.filter;
    }
    const cacheKey = `formkit_dropdown_${props.context.collection}_${stableStringify(query)}`;
    const collectionsStore = useCollectionsStore();
    const { data } = await collectionsStore.fetch(cacheKey, async () => {
      const r = await directus.items(props.context.collection).readByQuery(query);
      return r.data || [];
    }, { ttl: 15 * 60 * 1000 }); // 15 min — dropdown options change rarely
    response.value = data || [];
    optionsLoaded.value = true;

    // Set initial value after options are loaded
    await nextTick();
    if (props.context.value) {
      field.value = props.context.value;
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  }
});

// Watch for external updates to the value
watch(
  () => props.context.value,
  (newValue) => {
    // Only update if options are loaded to ensure proper label display
    if (optionsLoaded.value) {
      field.value = newValue;
    }
  }
);

watch(
  () => field.value,
  (newValue) => {
    props.context.node.input(newValue); // Notify FormKit of the value change
  }
);
</script>

<template>
 
  <FormKit
    type="autocomplete"
    :name="context.fieldname" 
    popover
    selection-appearance="option"
    :disabled="context.disabled"
    open-on-click
    :placeholder="context.placeholder"
    :empty-message="$t('No Items found')"
    v-model="field"
    :options="response.length
      ? response.map((item) => ({
          value: Object.values(item)?.[0] || '',
          label: `${Object.values(item)?.[1] || ''} ${Object.values(item)?.[2] || ''} ${
            Object.values(item)?.[3] || ''
          } ${Object.values(item)?.[4] || ''}`,
        }))
      : []"
  />
</template>

<style scoped>
.custom-selctor {
  min-height: 35px;
  padding: 8px;
}
.formkit-inner {
  position: relative;
}
.item-link {
  position: absolute;
  top: 40px;
  left: 8px;
}
</style>

<style>
[data-type="dbdropdown"] .formkit-inner {
  border: none;
  box-shadow: none;
}

[data-type="dbdropdown"] .formkit-inner:focus-within{
  box-shadow: none;
}

[data-type="dbdropdown"] .formkit-inner .formkit-inner {
  box-shadow: 0 0 0 1px #D0D5DD;
}
</style>